import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { IconButton, Grid } from '@mui/material';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { MdHelpOutline } from 'react-icons/md';
// @ts-ignore
import introJs from 'intro.js';
import 'intro.js/introjs.css';

import TabPanel, { a11yProps } from '@/components/tab-panel/tab-panel';
import UsersManagementTable from '@/screens/administration/components/users-management';
import Accounts from '@/screens/administration/components/accounts';
import MyAccountTab from '@/screens/administration/components/my-account-tab';
import SyncHistory from '@/screens/administration/components/sync-history';
import { getToken, subscribeToSession } from '@/lib/common';
import { AppContainer } from '@/components/ui';
import { hasUserAccessTo, refreshUserRoles } from '@/lib/permissions';
import { PermissionArea } from '@cloudcost/shared-types/users';

// @ts-ignore
TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

type AdminTab = {
  id: string;
  label: string;
  testId: string;
  dataIntro?: string;
  content: React.ReactNode;
};

function Administration() {
  const [, setSessionVersion] = useState(0);
  const isAdmin = hasUserAccessTo(PermissionArea.ADMIN_PANEL);
  const showCloudAccounts =
    isAdmin ||
    hasUserAccessTo(PermissionArea.SPECIAL_IS_CLOUD_ACCOUNT_OWNER);

  useEffect(() => {
    const unsubscribe = subscribeToSession(() => {
      setSessionVersion((version) => version + 1);
    });
    void refreshUserRoles();
    return unsubscribe;
  }, []);

  const tabs = useMemo((): AdminTab[] => {
    const items: AdminTab[] = [
      {
        id: 'my-account',
        label: 'My account',
        testId: 'tab-my-account',
        content: <MyAccountTab />,
      },
    ];
    if (isAdmin) {
      items.push({
        id: 'users',
        label: 'Users',
        testId: 'tab-users',
        dataIntro:
          'This Tab allow You to add users into the application. Every user should have a manager. The manager is informed if this user is running over their defined budget. The manager is also included in Disputes related to that user. You can also use this tab to reset users passwords.',
        content: <UsersManagementTable />,
      });
    }
    if (showCloudAccounts) {
      items.push({
        id: 'cloud-accounts',
        label: 'Cloud Accounts',
        testId: 'tab-cloud-accounts',
        dataIntro:
          'This Tab allow You to define the Azure Subscriptions that you would like to synchronize costs for. You should define each subscription that you want to synchronize, and what is the service account for that subscription. The guide for setting up the service account can be also found on this page.',
        content: <Accounts />,
      });
    }
    if (isAdmin) {
      items.push({
        id: 'sync-history',
        label: 'Sync history',
        testId: 'tab-sync-history',
        dataIntro:
          'Review recent cloud subscription synchronizations. Each row shows whether the sync succeeded or failed; expand it to see individual steps, finish times, and retry failed runs.',
        content: <SyncHistory />,
      });
    }
    return items;
  }, [isAdmin, showCloudAccounts]);

  const startOnboarding = () => {
    introJs().refresh();
    introJs()
      .setOptions({ tooltipClass: 'customTooltip' })
      .goToStepNumber(7)
      .start();
  };

  const [value, setValue] = React.useState(0);

  const handleChange = (_event: any, newValue: number) => {
    setValue(newValue);
  };

  if (!getToken()) {
    return <Navigate to="/" replace />;
  }

  const safeTabIndex = value < tabs.length ? value : 0;

  return (
    <AppContainer>
      <Grid container spacing={2} className="card100">
        <Grid item className="card100">
          <div className="card" style={{ height: '100%', width: '100%' }}>
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                <Tabs
                  value={safeTabIndex}
                  onChange={handleChange}
                  aria-label="administration tabs">
                  {tabs.map((tab, index) => (
                    <Tab
                      key={tab.id}
                      label={tab.label}
                      {...a11yProps(index)}
                      data-testid={tab.testId}
                      data-intro={tab.dataIntro}
                    />
                  ))}
                </Tabs>
                <IconButton
                  onClick={startOnboarding}
                  sx={{ marginLeft: 'auto' }}>
                  <MdHelpOutline
                    size={24}
                    style={{
                      color: '#5A6472',
                    }}
                  />
                </IconButton>
              </Box>
              {tabs.map((tab, index) => (
                <TabPanel key={tab.id} value={safeTabIndex} index={index}>
                  {tab.content}
                </TabPanel>
              ))}
            </Box>
          </div>
        </Grid>
      </Grid>
    </AppContainer>
  );
}

export default Administration;
